/**
 * RatesCard: the client's rates, on the record's Billing settings and the
 * client billing page (0132, B1-D3).
 *
 * One aligned grid. Hourly services first: Rate band | Rate | After-hours
 * rate | Change, each after-hours rate on its own service's row (admin has
 * none). Then, under "Per visit", the per-visit services with their one
 * rate. A per-visit service shows only on a client its service family
 * applies to (`family` on the tenant's band: a SPICE per-visit service on
 * a client with SPICE, a care-management one on a client with care
 * management); a client whose engagement is not known shows them all.
 * Below 32rem of card width (a phone, or 200% text) each row stacks, with
 * each amount labelled.
 *
 * Each amount cell reads the shared rule (bandRatesOf): the rate in effect
 * today with a quiet "from {date}"; a rate that starts later with its own
 * date ("$350.00 per visit, from November 1, 2026"), under today's rate
 * when there is one; "No rate on file" only when the band has neither.
 * The row's Rate history lists every other active row of both its bands,
 * with Archive for rate managers (0138). Archived rates leave the screen
 * by default (LP-40): the page's one archived line shows them under
 * their service's row, muted, with Restore for rate managers.
 *
 * Quiet on purpose. A missing rate blocks billing on THAT band only, and
 * the Pre-billing row for the note is where the block is named. A client
 * who never does SPICE has no SPICE rate, and nothing about that is wrong.
 *
 * Capability: canManageClientRates gets one Change per row (Set rate on a
 * row with nothing on file), opening the shared SetRateDialog for the
 * band and its after-hours rate together; and "Apply a rate set" on a
 * client with no active rates when the tenant has rate sets. Everyone else
 * (view_invoice holders) reads only.
 *
 * This card DISPLAYS rates. No dollar math happens here; the native event
 * writer snapshots the band's rate at write time.
 */

import { useState, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { AsyncBoundary } from '@/components/primitives/AsyncBoundary';
import { Button } from '@/components/primitives/Button';
import { Chip } from '@/components/primitives/Chip';
import { useArchivedItems } from '@/components/clientRecord/cardSpine';
import { ApplyRateSetDialog } from '@/components/ApplyRateSetDialog';
import { ArchiveRateDialog } from '@/components/ArchiveRateDialog';
import { SetRateDialog } from '@/components/SetRateDialog';
import { useTenant } from '@/lib/auth';
import { canManageClientRates } from '@/lib/capabilities';
import {
  bandFamily,
  pairPriceableBands,
  readPriceableRateBands,
  readRateBands,
  type PricedBandPair,
  type RateBandOption,
} from '@/lib/notes';
import type { ClientEngagement } from '@/lib/clientRecord';
import { readBandsOff } from '@/lib/bandsOff';
import { proseDate } from '@/lib/proseDate';
import { useTenantConfig } from '@/hooks/useTenantConfig';
import { readRateSets } from '@/lib/rateSets';
import {
  bandRatesOf,
  clientRatesKey,
  formatMoney,
  rateUnit,
  useClientRates,
  type ClientRateRowWire,
} from '@/lib/clientRates';

/** The grid's columns: the same template on every row and the header, so
 *  the amounts line up down the card. Below 32rem of card width a row
 *  stacks. */
const ROW_GRID = 'grid gap-x-4 gap-y-1 @lg:grid-cols-[minmax(0,1fr)_10rem_10rem_6rem] @lg:items-baseline';

/** One amount: today's rate, the one that starts later, or no rate. */
function AmountCell({
  band,
  rows,
  today,
  label,
}: {
  band: RateBandOption;
  rows: readonly ClientRateRowWire[];
  today: string;
  /** The column's name, shown beside the amount when the row stacks. */
  label: string;
}): ReactNode {
  const { current, upcoming } = bandRatesOf(rows, band.value, today);
  const unit = rateUnit(band.per_visit === true);
  return (
    <div className="min-w-0 text-sm">
      <span className="text-xs text-ink-soft @lg:hidden">{label}: </span>
      {current !== null && (
        <span className="text-ink" data-testid={`rates-current-${band.value}`}>
          {formatMoney(current.rate)} {unit}{' '}
          <span className="block text-xs text-ink-soft">from {proseDate(current.effective_from)}</span>
        </span>
      )}
      {upcoming !== null && (
        <span
          className={current !== null ? 'block text-xs text-ink-soft' : 'text-ink'}
          data-testid={`rates-upcoming-${band.value}`}
        >
          {current !== null ? 'then ' : ''}
          {formatMoney(upcoming.rate)} {unit}, from {proseDate(upcoming.effective_from)}
        </span>
      )}
      {current === null && upcoming === null && (
        <span className="italic text-ink-soft" data-register="quiet" data-testid={`rates-norate-${band.value}`}>
          No rate on file
        </span>
      )}
    </div>
  );
}

/** One service's row: its rate, its after-hours rate (hourly services),
 *  one Change, and the rate history of both its bands. */
function ServiceRow({
  pair,
  rows,
  today,
  canManage,
  perVisitGroup,
  onEdit,
  onArchive,
}: {
  pair: PricedBandPair;
  rows: readonly ClientRateRowWire[];
  today: string;
  canManage: boolean;
  /** In the Per visit group: no after-hours column. */
  perVisitGroup: boolean;
  onEdit: (pair: PricedBandPair) => void;
  /** Open the archive or restore confirm for one history row (0138). */
  onArchive: (band: RateBandOption, row: ClientRateRowWire) => void;
}): ReactNode {
  const [showHistory, setShowHistory] = useState(false);
  const { activity, afterHours } = pair;
  const own = bandRatesOf(rows, activity.value, today);
  const ah = afterHours === null ? null : bandRatesOf(rows, afterHours.value, today);
  const others: Array<{ band: RateBandOption; row: ClientRateRowWire }> = [
    ...own.others.map((row) => ({ band: activity, row })),
    ...(ah === null || afterHours === null ? [] : ah.others.map((row) => ({ band: afterHours, row }))),
  ];
  const history = others.filter((o) => o.row.is_active);
  const archived = others.filter((o) => !o.row.is_active);
  // LP-40: archived rates count toward the page's one archived line and
  // show here, under the row, only while it reads Hide.
  const archivedShown = useArchivedItems(archived.length);
  const anyOnFile =
    own.current !== null || own.upcoming !== null || (ah !== null && (ah.current !== null || ah.upcoming !== null));

  return (
    <div className="py-2.5" data-testid={`rates-row-${activity.value}`}>
      <div className={ROW_GRID}>
        <span className="text-sm font-medium text-sage-800">{activity.label}</span>
        <AmountCell band={activity} rows={rows} today={today} label="Rate" />
        {!perVisitGroup && afterHours !== null ? (
          <AmountCell band={afterHours} rows={rows} today={today} label="After hours" />
        ) : (
          // Per-visit services and admin have no after-hours rate: the
          // column stays, empty, so the row lines up.
          <span aria-hidden="true" className="hidden @lg:block" />
        )}
        <div className="@lg:justify-self-end">
          {canManage && (
            <Button variant="ghost" size="sm" data-testid={`rates-set-${activity.value}`} onClick={() => onEdit(pair)}>
              {anyOnFile ? 'Change' : 'Set rate'}
            </Button>
          )}
        </div>
      </div>
      {history.length > 0 && (
        <div className="mt-1">
          <Button
            variant="ghost"
            size="sm"
            data-testid={`rates-earlier-${activity.value}`}
            aria-expanded={showHistory}
            onClick={() => setShowHistory((v) => !v)}
          >
            {showHistory ? 'Hide rate history' : `Rate history (${history.length})`}
          </Button>
          {showHistory && (
            <ul className="mt-1 flex flex-col gap-0.5 pl-1 text-xs text-ink-soft" data-testid={`rates-earlier-list-${activity.value}`}>
              {history.map(({ band, row: r }) => (
                <li key={r.id} className="flex flex-wrap items-baseline gap-x-2">
                  <span data-testid={`rates-earlier-row-${r.id}`}>
                    {band.value !== activity.value ? 'After hours: ' : ''}
                    {formatMoney(r.rate)} {rateUnit(band.per_visit === true)} from {proseDate(r.effective_from)}
                  </span>
                  {canManage && (
                    <Button
                      variant="ghost"
                      size="sm"
                      data-testid={`rates-archive-${r.id}`}
                      className="min-h-0 px-1.5 py-0 text-xs"
                      onClick={() => onArchive(band, r)}
                    >
                      Archive
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      {archivedShown && archived.length > 0 && (
        <ul className="mt-1 flex flex-col gap-0.5 pl-1 text-xs text-ink-soft" data-testid={`rates-archived-list-${activity.value}`}>
          {archived.map(({ band, row: r }) => (
            <li key={r.id} className="flex flex-wrap items-center gap-x-2" data-testid={`rates-archived-${r.id}`}>
              <span className="opacity-60" data-testid={`rates-earlier-row-${r.id}`}>
                {band.value !== activity.value ? 'After hours: ' : ''}
                {formatMoney(r.rate)} {rateUnit(band.per_visit === true)} from {proseDate(r.effective_from)}
              </span>
              <Chip variant="neutral">Archived</Chip>
              {canManage && (
                <Button
                  variant="ghost"
                  size="sm"
                  data-testid={`rates-archive-${r.id}`}
                  className="min-h-0 px-1.5 py-0 text-xs"
                  onClick={() => onArchive(band, r)}
                >
                  Restore
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Whether a per-visit service applies to this client: its family is one
 *  of the client's services. An unknown engagement shows every service. */
function perVisitApplies(
  band: RateBandOption,
  bands: readonly RateBandOption[],
  engagement: ClientEngagement | undefined,
): boolean {
  if (engagement === undefined || engagement === 'both') return true;
  return bandFamily(band.value, bands) === engagement;
}

/** What a row's Change opens with: today's rate, or the one that starts
 *  later on its own date (so a corrected amount replaces it), and the
 *  after-hours rate likewise. */
function rowDialogProps(
  pair: PricedBandPair,
  rows: readonly ClientRateRowWire[],
  today: string,
): { band: RateBandOption; initialRate: string; initialFrom?: string; afterHours?: { band: RateBandOption; initialRate: string } } {
  const own = bandRatesOf(rows, pair.activity.value, today);
  const ownRow = own.current ?? own.upcoming;
  const ah =
    pair.afterHours === null || pair.activity.per_visit === true
      ? undefined
      : (() => {
          const r = bandRatesOf(rows, pair.afterHours.value, today);
          return { band: pair.afterHours, initialRate: (r.current ?? r.upcoming)?.rate ?? '' };
        })();
  return {
    band: pair.activity,
    initialRate: ownRow?.rate ?? '',
    initialFrom: own.current === null && own.upcoming !== null ? own.upcoming.effective_from : undefined,
    afterHours: ah,
  };
}

export interface RatesCardProps {
  clientId: string;
  /** The page-injected local day ('YYYY-MM-DD'). */
  today: string;
  /** clients.engagement: which per-visit services apply. */
  engagement?: ClientEngagement;
}

export function RatesCard({ clientId, today, engagement }: RatesCardProps): ReactNode {
  const { tenantSlug = '' } = useParams<{ tenantSlug: string }>();
  const queryClient = useQueryClient();
  const { whoami } = useTenant();
  const canManage = canManageClientRates(whoami);
  const { data: tenantConfig } = useTenantConfig(tenantSlug);
  // PRICEABLE bands, not activity bands. After-hours is settable here
  // but never appears in the note composer's picker: a CM logs care
  // management, or travel, and ticks a box — after-hours is not
  // something you do, it is when you did it. The flat list feeds
  // ApplyRateSetDialog (one amount per priceable band); `pairs` below
  // is this card's own row grouping (each activity band beside its
  // derived after-hours band).
  const bands = readPriceableRateBands(tenantConfig?.config);
  // A service switched off (billing.bands_off) has no row while it is off;
  // its rates stay on file and show again when it is switched on.
  const bandsOff = readBandsOff(tenantConfig?.config);
  const pairs = pairPriceableBands(tenantConfig?.config).filter((p) => !bandsOff.has(p.activity.value));
  const activityBands = readRateBands(tenantConfig?.config);
  const hourly = pairs.filter((p) => p.activity.per_visit !== true);
  const perVisit = pairs.filter(
    (p) => p.activity.per_visit === true && perVisitApplies(p.activity, activityBands, engagement),
  );
  const { rates, isLoading, isError, error, refetch } = useClientRates(tenantSlug, clientId);
  const [editing, setEditing] = useState<PricedBandPair | null>(null);
  const [archiving, setArchiving] = useState<{
    band: RateBandOption;
    row: ClientRateRowWire;
  } | null>(null);
  const [applying, setApplying] = useState(false);

  // "Apply a rate set": rate managers, a client with NO active rate row
  // (archived-only history counts as none), and at least one set to
  // choose from. Never an overwrite: a card with a rate in force offers
  // Set rate / Change per band instead.
  const rateSets = readRateSets(tenantConfig?.config);
  const noActiveRates = rates !== null && rates.every((r) => !r.is_active);
  const canApplySet = canManage && noActiveRates && rateSets.length > 0;

  return (
    <div
      className="rounded-card bg-paper shadow-sh1 border border-sage-800/[8%] p-6"
      data-testid="rates-card"
    >
      <h3 className="font-display text-lg text-sage-800 mb-3">Rates</h3>

      <AsyncBoundary isLoading={isLoading} isError={isError} error={error} onRetry={refetch}>
        {canApplySet && (
          <div className="mb-3">
            <Button
              variant="secondary"
              size="sm"
              data-testid="rates-apply-set"
              onClick={() => setApplying(true)}
            >
              Apply a rate set
            </Button>
          </div>
        )}
        {rates !== null && (
          <div className="@container" data-testid="rates-grid">
            <div
              className={`${ROW_GRID} hidden border-b border-sage-800/[8%] pb-1.5 text-label font-semibold uppercase tracking-[0.12em] text-sage-400 @lg:grid`}
              aria-hidden="true"
            >
              <span>Rate band</span>
              <span>Rate</span>
              <span>After hours</span>
              <span />
            </div>
            <div className="divide-y divide-sage-800/[6%]">
              {hourly.map((pair) => (
                <ServiceRow
                  key={pair.activity.value}
                  pair={pair}
                  rows={rates}
                  today={today}
                  canManage={canManage}
                  perVisitGroup={false}
                  onEdit={setEditing}
                  onArchive={(b, row) => setArchiving({ band: b, row })}
                />
              ))}
            </div>
            {perVisit.length > 0 && (
              <section className="mt-3" data-testid="rates-per-visit">
                <h4 className="border-b border-sage-800/[8%] pb-1.5 text-label font-semibold uppercase tracking-[0.12em] text-sage-400">
                  Per visit
                </h4>
                <div className="divide-y divide-sage-800/[6%]">
                  {perVisit.map((pair) => (
                    <ServiceRow
                      key={pair.activity.value}
                      pair={pair}
                      rows={rates}
                      today={today}
                      canManage={canManage}
                      perVisitGroup
                      onEdit={setEditing}
                      onArchive={(b, row) => setArchiving({ band: b, row })}
                    />
                  ))}
                </div>
              </section>
            )}
          </div>
        )}
      </AsyncBoundary>

      {editing && rates !== null && (
        <SetRateDialog {...rowDialogProps(editing, rates, today)}
          clientId={clientId}
          today={today}
          title={editing.activity.label}
          onSaved={(next) => queryClient.setQueryData(clientRatesKey(tenantSlug, clientId), next)}
          onClose={() => setEditing(null)}
        />
      )}

      {archiving && (
        <ArchiveRateDialog
          clientId={clientId}
          rate={archiving.row}
          bandLabel={archiving.band.label}
          archived={archiving.row.is_active}
          onDone={(rates) => queryClient.setQueryData(clientRatesKey(tenantSlug, clientId), rates)}
          onClose={() => setArchiving(null)}
        />
      )}

      {applying && (
        <ApplyRateSetDialog
          clientId={clientId}
          sets={rateSets}
          bands={bands}
          today={today}
          onApplied={(rates) => queryClient.setQueryData(clientRatesKey(tenantSlug, clientId), rates)}
          onClose={() => setApplying(false)}
        />
      )}
    </div>
  );
}
